import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@component/dialog";
import { Button } from "@component/button";
import { Card, CardContent } from "@component/card";
import {
  CheckCircle2Icon,
  FileTextIcon,
  VideoIcon,
  ClipboardListIcon,
  BriefcaseIcon,
    ArrowRightIcon,
} from "lucide-react";
import { cn } from "@util/tailwind";
import { JobCreateRequest } from "@service/employer";
import { Company } from "@type/company";
import SWEExperienceSurvey from "@container/eligibility-check-job";
import PreviewInterviewCardHeader from "@component/preview-waiting-room/preview-interview-card-header";
import PreviewVideoSection from "@component/preview-waiting-room/preview-video-section";
import PreviewInterviewSidebar from "@component/preview-waiting-room/preview-interview-sidebar";
import { ProgressBar } from "@component/progress-bar";
import PreviewQuiz from "@component/preview-waiting-room/preview-quiz";
import PreviewEmailOTPVerify from "@component/preview-waiting-room/preview-email-otp-verify";
import PreviewConfirmDetails from "@component/preview-waiting-room/preview-confirm-details";
import PreviewJobDetail from "@component/preview-waiting-room/preview-job-detail";
import { getAssessment } from "@service/assessment";
import { FormAssessment, InterviewAssessment } from "@type/assessments";

interface JobFlowPreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobData?: JobCreateRequest;
  selectedAssessmentForm?: string;
  selectedAssessmentFormId?: string;
  selectedAssessmentInterview?: string;
  selectedAssessmentInterviewId?: string;
  companyInfo?: Company;
}

type FlowStep =
  | "job-details"
  | "application-form"
  | "introduction-screen"
  | "ai-interview"
  | "form-assessment"
  | "eligibility-check"
  | "employment-details"
  | "submission"
  | "assessment-complete";

export default function JobPreviewDialogV2({
  isOpen,
  onClose,
  jobData,
  selectedAssessmentForm,
  selectedAssessmentFormId,
  selectedAssessmentInterview,
  selectedAssessmentInterviewId,
  companyInfo,
}: JobFlowPreviewDialogProps) {
  const [activeTab, setActiveTab] = useState<"application" | "assessment">(
    "application"
  );
  const [selectedStep, setSelectedStep] = useState<FlowStep>("job-details");
  const [formAssessment, setFormAssessment] = useState<FormAssessment | null>(null);
  const [interviewLanguage, setInterviewLanguage] = useState<string>("en-US");
  const [interviewDuration, setInterviewDuration] = useState<number>(20);
  const [interviewDescription, setInterviewDescription] = useState<string>("");

  useEffect(() => {
    if (isOpen && selectedAssessmentFormId) {
      getAssessment(selectedAssessmentFormId).then((data) => {
        if (data?.content) {
          setFormAssessment(data as unknown as FormAssessment);
        }
      });
    }
    if (isOpen && selectedAssessmentInterviewId) {
      getAssessment(selectedAssessmentInterviewId).then((data) => {
        const interviewData = data as unknown as InterviewAssessment;
        if (interviewData?.content) {
          if (interviewData.content.interviewLanguage) {
            setInterviewLanguage(interviewData.content.interviewLanguage);
          }
          if (interviewData.content.maxDuration) {
            setInterviewDuration(Math.round(interviewData.content.maxDuration / 60));
          }
          if (interviewData.content.description) {
            setInterviewDescription(interviewData.content.description);
          }
        }
      });
    }
  }, [isOpen, selectedAssessmentFormId, selectedAssessmentInterviewId]);

  // Get the first available step for assessment flow
  const getFirstAssessmentStep = (): FlowStep => {
    return "introduction-screen";
  };

  // Application Flow Steps
  const applicationFlowSteps = [
    {
      id: "job-details" as FlowStep,
      title: "Job Detail Page",
      icon: BriefcaseIcon,
      description:
        "Candidate reviews the job details and submits their application.",
    },
    {
      id: "application-form" as FlowStep,
      title: "Verify Email",
      icon: FileTextIcon,
      description: "Candidate verifies their email using a 6-digit OTP.",
    },
    {
      id: "employment-details" as FlowStep,
      title: "Confirm Details",
      icon: ClipboardListIcon,
      description: "Candidate reviews and edits details parsed from their CV.",
    },
      {
          id: "submission" as FlowStep,
          title: "Application Complete",
          icon: CheckCircle2Icon,
          description: "Candidate sees confirmation of their submitted application.",
      },
  ];

  // Check if any eligibility check field is enabled
  const workAuth = jobData?.work_authorization_metadata;
  const hasEligibilityChecks = workAuth?.collect_employment_status ||
    workAuth?.collect_expected_salary ||
    workAuth?.collect_start_availability ||
    workAuth?.collect_current_salary ||
    workAuth?.collect_relocation_willingness ||
    workAuth?.collect_weekend_shift_willingness;

  // Assessment Flow Steps
  const assessmentFlowSteps = [
    {
      id: "introduction-screen" as FlowStep,
      title: "Introduction Screen",
      icon: FileTextIcon,
      description: "Candidate views the screening overview and instructions.",
      enabled: true,
    },
    {
      id: "ai-interview" as FlowStep,
      title: "AI Interview",
      icon: VideoIcon,
      description: `Candidate completes the AI interview:\n${selectedAssessmentInterview && selectedAssessmentInterview !== "None" ? selectedAssessmentInterview : "General Introductory Interview"}.`,
      enabled: true,
    },
    {
      id: "form-assessment" as FlowStep,
      title: "Form",
      icon: ClipboardListIcon,
      description: `Candidate completes the form:\n${selectedAssessmentForm || "Complete the form"}.`,
      enabled: selectedAssessmentForm && selectedAssessmentForm !== "None",
    },
    {
      id: "eligibility-check" as FlowStep,
      title: "Eligibility Check",
      icon: BriefcaseIcon,
      description: "Candidate provides required employment details.",
      enabled: hasEligibilityChecks,
    },
    {
      id: "assessment-complete" as FlowStep,
      title: "Screening Complete",
      icon: CheckCircle2Icon,
      description: "Candidate sees confirmation of their completed screening.",
      enabled: true,
    },
  ].filter((step) => !!step.enabled);

  const currentFlowSteps =
    activeTab === "application" ? applicationFlowSteps : assessmentFlowSteps;

  // Function to advance to next step
  const goToNextStep = () => {
    const currentIndex = currentFlowSteps.findIndex(
      (step) => step.id === selectedStep
    );
    if (currentIndex < currentFlowSteps.length - 1) {
      setSelectedStep(currentFlowSteps[currentIndex + 1].id);
    }
  };

  const renderFlowchart = () => {
    return (
      <div>
        <div className="space-y-2">
          {currentFlowSteps.map((step, index) => {
            const Icon = step.icon;
            const isSelected = selectedStep === step.id;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setSelectedStep(step.id)}
                className={cn(
                  "w-full text-left p-4 rounded-lg border-2 transition-all hover:border-blue-400 hover:shadow-md",
                  isSelected
                    ? "border-blue-600 bg-blue-50 shadow-md"
                    : "border-gray-200 bg-white"
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold",
                      isSelected
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700"
                    )}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4
                      className={cn(
                        "font-medium text-sm",
                        isSelected ? "text-blue-900" : "text-gray-900"
                      )}
                    >
                      {step.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {step.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    switch (selectedStep) {
      case "introduction-screen":
        return (
          <div className="flex justify-center items-center min-h-[500px]">
                <Card className="w-full max-w-lg shadow-md">
                    <CardContent className="p-6 space-y-6">
                        <div className="flex flex-col items-center text-center space-y-6">
                            <div className="space-y-5">
                                <div className="text-center space-y-3">
                                    <h2 className="text-2xl font-bold text-gray-900">
                                        Begin Your AI Screening
                                    </h2>
                                    <div className="space-y-1">
                                        <p className="text-sm text-gray-600 font-medium">
                                            You're about to start the screening for your job application.
                                        </p>
                                        <p className="text-sm text-gray-600 font-medium">
                                            This link will expire on{" "}
                                            <span className="font-bold text-gray-900">
                                                {new Date(Date.now() + (jobData?.application_expiry_days || 7) * 86400000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                                            </span>
                                            {" "}(in <span className="font-bold text-gray-900">{jobData?.application_expiry_days || 7} days</span>).
                                        </p>
                                    </div>

                                    <div className="space-y-4 mx-auto">
                                        <div className="relative overflow-hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/30">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/40 rounded-full blur-3xl" />
                                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl" />

                                            <div className="relative p-6">
                                                <div className="flex flex-col items-center space-y-4 text-center">
                                                    <div className="relative flex-shrink-0">
                                                        <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-md" />
                                                        <div className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden flex justify-center items-center bg-white ring-1 ring-blue-200">
                                  {companyInfo?.preferences?.company_logo ? (
                                    <img
                                      src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${companyInfo.preferences.company_logo}`}
                                      alt={jobData?.company_name || "Company"}
                                      className="w-12 object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.parentElement!.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8 text-blue-600"><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/></svg>';
                                      }}
                                    />
                                  ) : (
                                      <BriefcaseIcon className="w-8 h-8 text-blue-600" />
                                  )}
                                                        </div>
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                                                            {jobData?.job_title || "Software Engineer"}
                                                        </h3>
                                                        <p className="text-sm text-gray-600 font-medium mb-2">
                                                            {jobData?.company_name || "Company"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    onClick={goToNextStep}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                                    style={{ paddingTop: "16px", paddingBottom: "16px" }}
                                >
                                    Begin Screening
                                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                                </Button>
                            </div>

                            <div className="text-center">
                                <p className="text-xs text-gray-500">
                                    By proceeding, you agree to the{" "}
                                    <a
                                        href="https://ourteam.ai/terms-of-service"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline text-gray-700 hover:text-gray-900"
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    at ourteam.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );

      case "job-details":
        return (
          <PreviewJobDetail jobData={jobData} onApply={goToNextStep} />
        );

      case "application-form":
        return (
          <div className="flex justify-center items-center min-h-[500px]">
            <PreviewEmailOTPVerify onNextStep={goToNextStep} />
          </div>
        );

      case "ai-interview":
        return (
          <Card className="border-0 overflow-hidden">
            <CardContent className="!p-0">
                    <div className="w-full h-full bg-white flex flex-col">
                <div className="flex-1 px-4 pb-0">
                  <div className="h-full flex flex-col">
                    <div className="mx-auto w-full bg-white overflow-hidden mt-4">
                      <PreviewInterviewCardHeader
                        title={selectedAssessmentInterview && selectedAssessmentInterview !== "None" ? selectedAssessmentInterview : "General Introductory Interview"}
                        description={interviewDescription}
                        interviewLanguage={interviewLanguage}
                        durationInMinutes={interviewDuration}
                      />

                      <div className="grid grid-cols-[1fr_auto] gap-6">
                        <PreviewVideoSection />
                        <PreviewInterviewSidebar
                          accentColor="#2563eb"
                          showBranding={true}
                          timeRemaining="01:29"
                          onStartInterview={goToNextStep}
                          interviewLanguage={interviewLanguage}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                </CardContent>
            </Card>
        );

      case "form-assessment":
        return formAssessment ? (
          <div className="flex justify-center items-center min-h-[500px]">
            <PreviewQuiz
              assessment={formAssessment}
              onSubmit={goToNextStep}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-gray-500">Loading form...</p>
          </div>
        );

      case "employment-details":
        return (
          <div className="flex justify-center items-start">
            <PreviewConfirmDetails onSubmit={goToNextStep} />
          </div>
        );

        case "eligibility-check":
            return (
              <div className="flex justify-center items-center min-h-[500px]">
                <SWEExperienceSurvey
                  is_view
                  is_show_button
                  onButtonClick={goToNextStep}
                  work_authorization_metadata={jobData?.work_authorization_metadata}
                  location_metadata={jobData?.location_metadata ?? undefined}
                />
              </div>
            );

      case "assessment-complete":
        return (
          <div className="flex justify-center items-center min-h-[500px]">
            <div
              className="bg-white rounded-3xl p-8 shadow-2xl text-center w-full max-w-lg mx-auto"
              style={{
                boxShadow: "0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)",
              }}
            >
              <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full flex items-center justify-center bg-green-100 text-green-700 mx-auto mb-6">
                <VideoIcon className="h-6 sm:h-10 w-6 sm:w-10" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 text-center">
                Screening Completed!
              </h1>
              <p className="text-gray-600 text-center">
                Thank you for completing the{" "}
                <b>{selectedAssessmentInterview && selectedAssessmentInterview !== "None" ? selectedAssessmentInterview : "General Introductory Interview"}</b>{" "}
                with ourteam.
                <br />
                <br />
                You will be notified of your application status via email.
              </p>
            </div>
          </div>
        );

      case "submission":
        return (
          <div className="flex justify-center items-center min-h-[500px]">
            <div className="bg-white rounded-xl p-4 sm:p-8 w-full max-w-3xl">
                <div className="text-center mb-4 sm:mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-2 sm:mb-4">
                        <CheckCircle2Icon className="w-10 h-10 text-green-600" />
              </div>
                    <h2 className="text-2xl font-bold">
                        Application submitted successfully!
              </h2>
              <p className="text-black mt-2">
                Your application for <b>{jobData?.job_title || "this position"}</b> has been received.<br />
                You'll receive updates on your application status via email.
              </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
                    <h3 className="text-lg font-medium mb-4">Application Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">Job Title</p>
                            <p className="font-medium">{jobData?.job_title || ""}</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">Company</p>
                            <p className="font-medium">{jobData?.company_name || ""}</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">Application ID</p>
                            <p className="font-medium">PREVIEW-0001</p>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-500">Applied Date</p>
                            <p className="font-medium">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</p>
                        </div>
              </div>
            </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] h-[100vh] 2xl:max-w-[90vw] 2xl:h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        {/* Header with Tabs - Fixed */}
        <DialogHeader className="px-6 pt-6 pb-6 flex-shrink-0">
          <div
            className="flex items-start justify-between gap-6"
            style={{
              paddingTop: 0,
              paddingRight: 24,
              paddingBottom: 0,
              paddingLeft: 0,
            }}
          >
            <div className="flex-1">
              <DialogTitle className="text-[24px] font-bold">
                Preview Job Flows
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-1">
                Understand how your application and screening flows for this
                role will appear to your candidates.
              </p>
            </div>

            {/* Custom tabs aligned to the right */}
                      <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground flex-shrink-0 mr-5">
              <button
                type="button"
                onClick={() => {
                  setActiveTab("application");
                  setSelectedStep("job-details");
                }}
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                  activeTab === "application"
                    ? "bg-background text-foreground shadow-sm"
                    : "hover:bg-background/50"
                )}
              >
                Job Application Flow
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveTab("assessment");
                  setSelectedStep(getFirstAssessmentStep());
                }}
                className={cn(
                  "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                  activeTab === "assessment"
                    ? "bg-background text-foreground shadow-sm"
                    : "hover:bg-background/50"
                )}
              >
                Job Screening Flow
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-hidden">
          <div className="grid grid-cols-[380px_1fr] h-full">
            {/* Left: Flowchart - Fixed height */}
            <div
              className="bg-background p-6 overflow-y-auto"
              style={{
                paddingTop: 24,
                paddingRight: 24,
                paddingBottom: 24,
                paddingLeft: 32,
              }}
            >
              <div className="mb-4">
                <h3 className="font-semibold text-[18px] mb-2 text-foreground">
                  {activeTab === "application"
                    ? "Application Flow"
                    : "Screening Flow"}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {activeTab === "application" ? (
                    <>
                      This shows the steps candidates complete to{" "}
                      <span className="font-bold">submit an application</span>.
                    </>
                  ) : (
                    <>
                      This shows the steps candidates complete after{" "}
                      <span className="font-bold">
                        receiving a screening invitation
                      </span>
                      .
                    </>
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  Select a step to preview it, or interact with the preview to
                  automatically proceed to the next step.
                </p>
              </div>
              <div className="min-h-[400px]">{renderFlowchart()}</div>
            </div>

            {/* Right: Preview Screen - Fixed position, no movement */}
            <div
              className="bg-background p-6 overflow-y-auto"
              style={{
                paddingTop: 24,
                paddingRight: 32,
                paddingBottom: 24,
                paddingLeft: 24,
              }}
            >
              <div className="w-full flex flex-col">
                <div className="bg-background p-8 min-h-[600px] border border-gray-300 rounded-lg shadow-sm flex-1">
                  {renderPreview()}

                  {activeTab === "application" && selectedStep !== "job-details" && (() => {
                  const appLabelMap: Record<string, string> = {
                    "job-details": "Apply Job",
                    "application-form": "Verify Email",
                    "employment-details": "Confirm Details",
                    "submission": "Complete",
                  };
                  const currentIndex = applicationFlowSteps.findIndex((s) => s.id === selectedStep);
                  return (
                    <div className="mt-10">
                      <ProgressBar
                        currentStep={currentIndex + 1}
                        steps={applicationFlowSteps.map((step, idx) => ({
                          label: appLabelMap[step.id] || step.title,
                          isActive: step.id === selectedStep,
                          isCompleted: currentIndex > idx,
                          isHide: step.id === "job-details",
                        }))}
                      />
                    </div>
                  );
                })()}
                {activeTab === "assessment" && selectedStep !== "introduction-screen" && (() => {
                  const progressLabelMap: Record<string, string> = {
                    "introduction-screen": "Introduction",
                    "ai-interview": "AI Interview",
                    "form-assessment": "Form",
                    "eligibility-check": "Eligibility Check",
                    "assessment-complete": "Complete",
                  };
                  const currentIndex = assessmentFlowSteps.findIndex((s) => s.id === selectedStep);
                  return (
                    <div className="mt-5">
                      <ProgressBar
                        currentStep={currentIndex + 1}
                        steps={assessmentFlowSteps.map((step, idx) => ({
                          label: progressLabelMap[step.id] || step.title,
                          isActive: step.id === selectedStep,
                          isCompleted: currentIndex > idx,
                          isHide: step.id === "introduction-screen",
                        }))}
                      />
                    </div>
                  );
                })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
